import { useRef, useCallback } from "react";
import { v4 as uuidv4 } from "uuid";
import { useChat } from "../context/ChatContext";
import { usePage, PAGES } from "../context/PageContext";
import { ConversationReplay, ReplayTurn } from "../utils/replay-api";
import { ConversationMessage } from "../utils/web-api";

const STREAM_CHUNK_SIZE = 4;
const STREAM_INTERVAL_MS = 20;

export function useConversationReplay() {
  const { selectConversationForReplay, appendMessage, dispatchStreamEvent, flushStreamingMessage } = useChat();
  const { showPage } = usePage();

  const timerIdsRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const streamTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearAllTimers = useCallback(() => {
    timerIdsRef.current.forEach(clearTimeout);
    timerIdsRef.current = [];
    if (streamTimerRef.current !== null) {
      clearInterval(streamTimerRef.current);
      streamTimerRef.current = null;
    }
  }, []);

  const dispatchTurn = useCallback((turn: ReplayTurn, conversationId: string) => {
    if (turn.type === "user") {
      appendMessage({
        id: uuidv4(),
        propelAuthUserID: turn.userId,
        createdAt: new Date().toISOString(),
        propelAuthOrgID: "",
        conversationId,
        content: turn.content,
        turnType: "user",
        turnArtifacts: {} as Record<string, never>,
      } as ConversationMessage);
      return;
    }

    const artifactType = turn.artifacts.type as string | undefined;

    // addin_content_request would trigger real document reads — skip in replay
    if (artifactType === "addin_content_request") return;

    if (artifactType === "text" && turn.content) {
      if (streamTimerRef.current !== null) {
        clearInterval(streamTimerRef.current);
        streamTimerRef.current = null;
      }
      let charPos = 0;
      streamTimerRef.current = setInterval(() => {
        charPos += STREAM_CHUNK_SIZE;
        const done = charPos >= turn.content.length;
        const chunk = turn.content.slice(charPos - STREAM_CHUNK_SIZE, charPos);
        dispatchStreamEvent({ type: "text", text: chunk }, conversationId);
        if (done) {
          clearInterval(streamTimerRef.current!);
          streamTimerRef.current = null;
        }
      }, STREAM_INTERVAL_MS);
      return;
    }

    if (artifactType === "tool_result") {
      // Strip `text` field so dispatchStreamEvent reads `content` (the full accumulated value)
      const { text: _text, ...rest } = turn.artifacts as Record<string, unknown>;
      dispatchStreamEvent({ ...rest, content: turn.content }, conversationId);
      return;
    }

    dispatchStreamEvent(turn.artifacts, conversationId);
  }, [appendMessage, dispatchStreamEvent]);

  const play = useCallback((replay: ConversationReplay, startUserTurnIndex: number) => {
    clearAllTimers();
    flushStreamingMessage();

    selectConversationForReplay(replay.conversationId);
    showPage(PAGES.chatPage);

    const startTurn = replay.turns.find(t => t.userTurnIndex === startUserTurnIndex);
    const offsetMs = startTurn?.relativeMs ?? 0;
    const turnsToPlay = replay.turns.filter(t => t.userTurnIndex >= startUserTurnIndex);

    turnsToPlay.forEach((turn) => {
      const timerId = setTimeout(
        () => dispatchTurn(turn, replay.conversationId),
        turn.relativeMs - offsetMs,
      );
      timerIdsRef.current.push(timerId);
    });

    if (turnsToPlay.length > 0) {
      const lastTurn = turnsToPlay[turnsToPlay.length - 1];
      const textStreamMs = lastTurn.artifacts.type === "text"
        ? Math.ceil(lastTurn.content.length / STREAM_CHUNK_SIZE) * STREAM_INTERVAL_MS
        : 0;
      const flushDelay = lastTurn.relativeMs - offsetMs + textStreamMs;
      timerIdsRef.current.push(setTimeout(flushStreamingMessage, flushDelay));
    }
  }, [clearAllTimers, flushStreamingMessage, selectConversationForReplay, showPage, dispatchTurn]);

  const stop = useCallback(() => {
    clearAllTimers();
    flushStreamingMessage();
  }, [clearAllTimers, flushStreamingMessage]);

  return { play, stop };
}
